import Phaser from "phaser";
import aStar from "./a_star_solver.js";
import { EnhancedScoring } from './EnhancedScoring.js';
import { getRandomOutcome } from "./mysteryOutcomes";

export default class CrazyMazeScene extends Phaser.Scene {
  constructor(difficulty = "medium", cellSize = 25) {
    super("CrazyMazeScene");
    this.mysteryBoxActive = false;
    // 🐛 DEBUG: Check what difficulty is being passed
    
    
    // 🔧 FIXED: Properly handle difficulty parameter
    if (typeof difficulty === 'object') {
      // If difficulty is passed as {difficulty: "easy"}, extract it
      this.difficulty = difficulty.difficulty || "medium";
      
    } else {
      // If difficulty is passed as string
      this.difficulty = difficulty || "medium";
    }
    
    // 🔧 FALLBACK: Read from localStorage if needed
    if (!this.difficulty || this.difficulty === "medium") {
      const savedDifficulty = localStorage.getItem("maze-difficulty");
      if (savedDifficulty) {
        this.difficulty = savedDifficulty;
        
      }
    }
    
    
    this.cellSize = cellSize;
    
    const presets = {
      easy:   { grid: 15, loops: 8,  timeMul: 1, stepMul:  5 },
      medium: { grid: 21, loops: 0,  timeMul: 2, stepMul: 10 },
      hard:   { grid: 31, loops: 0,  timeMul: 3, stepMul: 15 }
    };
    const D = presets[this.difficulty] ?? presets.medium;

    this.scoringSystem = new EnhancedScoring(this.difficulty);
    this.totalGems = 0;
    this.gridSize = D.grid;
    this.timeMultiplier = D.timeMul;
    this.stepMultiplier = D.stepMul;
    this.loopsToRemove = D.loops;

    this.score = 0;
    this.wallHits = 0;
    this.gemsCollected = 0;
    this.goldenGemCollected = false;
    this.stepsTaken = 0;
    this.startTime = null;
    this.lastScoreEmit = 0;
    this.pathWorker = null;

    this.playerEmoji = "🐼";
    this.gemEmoji = "💎";
    
    this.goldenGemKey = null;
    this.hasCompanion = false;
this.companionSprite = null;

  }

  preload() {
  this.load.audio("gem", "audio/gem.mp3");
  this.load.audio("pop", "audio/pop.mp3");
  this.load.audio("gem-shower", "audio/gem-shower.mp3");

}



triggerDemonCurse() {
  const stolenCount = 5;
  let actualStolen = 0;

 

  if (this.gemsCollected >= stolenCount) {
    this.gemsCollected -= stolenCount;
    actualStolen = stolenCount;
    
  } else if (this.gemMap && this.gemMap.size > 0) {
    const keys = Array.from(this.gemMap.keys());
    for (let i = 0; i < stolenCount && keys.length > 0; i++) {
      const key = keys.pop();
      const gem = this.gemMap.get(key);
if (gem?.text === "🌟") continue; // 🚫 Skip golden gem

if (gem) {
  gem.destroy();
  this.gemMap.delete(key);
  actualStolen++;
}

    }

    this.totalGems = this.gemMap.size;
    
  }

  if (actualStolen > 0) {
    this.score -= 50;
    this.maybeEmitScore();
  }

  // ✅ Emit UI update regardless
  this.externalEvents?.emit("gemUpdate", this.gemsCollected, this.totalGems);

  const msg = this.add.text(
    this.player.x,
    this.player.y - this.cellSize,
    `👹 -${actualStolen} gems & -50 pts`,
    {
      fontSize: `${this.cellSize * 0.4}px`,
      color: "#ff0000",
      fontStyle: "bold"
    }
  ).setOrigin(0.5).setDepth(5);

  this.tweens.add({
    targets: msg,
    y: msg.y - 30,
    alpha: 0,
    duration: 1500,
    ease: "Cubic.easeOut",
    onComplete: () => msg.destroy()
  });
}





triggerGemShower() {
  const gemEmoji = "💎";
  const totalGems = 15; // Increase for a richer visual effect
  const screenWidth = this.actualMazeWidth;

  for (let i = 0; i < totalGems; i++) {
    const x = Phaser.Math.Between(0, screenWidth);
    const delay = Phaser.Math.Between(0, 800);

    const gem = this.add.text(
      x,
      -50, // Start above the maze (top of screen)
      gemEmoji,
      { fontSize: `${this.cellSize * 0.5}px` }
    ).setOrigin(0.5).setDepth(1000); // Float above maze content

    this.time.delayedCall(delay, () => {
      this.tweens.add({
        targets: gem,
        y: this.actualMazeHeight + 50, // Fall beyond bottom of maze
        duration: 1200,
        ease: 'Cubic.easeIn',
        onComplete: () => gem.destroy()
      });
    });
  }

  // Play sound only once, at start
  this.playSoundSafe("gem-shower");
}





  create() {
    this.startTime = this.time.now;
    this.stepsTaken = 0;
    this.createMaze();
    this.drawMaze();
    this.createPlayer();
    this.createGems();
    this.totalGems = this.gemMap.size;
    this.setGoal();
    this.drawGoal();
    

    this.cursors = this.input.keyboard.createCursorKeys();
    this.pathWorker = new Worker(new URL("./pathWorker.js", import.meta.url), { type: "module" });
    
    // 🔧 EMIT initial updates
    this.externalEvents?.emit("stepUpdate", 0);
    this.externalEvents?.emit("gemUpdate", 0, this.totalGems);
    
    // Initial centering after everything is created
    this.time.delayedCall(100, () => {
      this.centerViewOnPlayer(true);
    });
  }
  move(dx, dy) {
    if (this.invertedControls) {
    dx = -dx;
    dy = -dy;
  }
  
  const nx = this.playerGrid.x + dx;
  const ny = this.playerGrid.y + dy;

  if (!this.isValidMove(nx, ny)) {
    this.wallHits++;
    this.score -= 5;
    this.maybeEmitScore();
    return;
  }

  this.playerGrid.x = nx;
  this.playerGrid.y = ny;
  this.player.setPosition(
    nx * this.cellSize + this.cellSize / 2,
    ny * this.cellSize + this.cellSize / 2
  );

  if (this.hasCompanion && this.companionSprite && this.player) {
    const t = this.time.now / 250;
    const floatOffset = Math.sin(t) * 5;
    this.companionSprite.setPosition(this.player.x + 10, this.player.y - 15 + floatOffset);
  }

  this.updateViewport();
  this.stepsTaken++;
  this.externalEvents?.emit("stepUpdate", this.stepsTaken);

  const key = `${nx},${ny}`;

  if (this.mysteryBoxActive) {
    return;
  } else if (this.mysteryBoxes.has(key)) {
    this.mysteryBoxActive = true;
    this.mysteryBoxes.delete(key);
    const boxEmoji = this.children.getByName(`box-${key}`);
    boxEmoji?.destroy();
    this.scene.pause();
    this.externalEvents?.emit("mysteryBoxTriggered", { x: nx, y: ny, key });
    return;
  }

  if (this.gemMap.has(key)) {
    const gem = this.gemMap.get(key);
    this.gemMap.delete(key);
    this.gemsCollected++;

    const isGoldenGem = key === this.goldenGemKey;
    const bonus = isGoldenGem ? 200 : 10;
    const emoji = isGoldenGem ? "🪄" : "✨";

    if (isGoldenGem) {
      this.goldenGemCollected = true;
      this.score += 200;
      this.triggerGemShower();
    } else {
      this.score += 10;
      this.playSoundSafe("pop", { volume: 0.6 });
    }

    this.tweens.add({
      targets: gem,
      scale: 1.5,
      alpha: 0,
      duration: 300,
      onComplete: () => gem.destroy()
    });

    const scorePop = this.add.text(
      this.player.x,
      this.player.y - this.cellSize / 1.5,
      `${emoji} +${bonus}`,
      {
        fontSize: `${this.cellSize * 0.45}px`,
        fill: isGoldenGem ? "#ffc107" : "#4caf50",
        fontStyle: "bold"
      }
    ).setOrigin(0.5).setDepth(10);

    this.tweens.add({
      targets: scorePop,
      y: scorePop.y - 30,
      alpha: 0,
      duration: 1200,
      ease: "easeOut",
      onComplete: () => scorePop.destroy()
    });

    this.externalEvents?.emit("gemUpdate", this.gemsCollected, this.totalGems);
    this.maybeEmitScore();
  }

  if (this.hasCompanion && this.gemMap.size > 0) {
  const px = this.playerGrid.x;
  const py = this.playerGrid.y;

  for (const [gkey, gem] of this.gemMap.entries()) {
    const [gx, gy] = gkey.split(",").map(Number);
    const dx = Math.abs(gx - px);
    const dy = Math.abs(gy - py);
    const distance = dx + dy;

    if (distance <= 2) {
      this.gemMap.delete(gkey);
      gem.destroy();
      this.gemsCollected++;

      const isGoldenGem = gkey === this.goldenGemKey;

      if (isGoldenGem) {
        this.goldenGemCollected = true;
        this.score += 200;
        this.triggerGemShower?.();
        this.playSoundSafe("gem-shower");
      } else {
        this.score += 10;
        this.playSoundSafe("pop", { volume: 0.6 });
      }

      this.externalEvents?.emit("gemUpdate", this.gemsCollected, this.totalGems);
      this.maybeEmitScore();
    }
  }
}


  if (nx === this.goalGrid.x && ny === this.goalGrid.y) {
    const elapsed = Math.floor((this.time.now - this.startTime) / 1000);
    this.scene.pause();
    this.pathWorker.onmessage = ({ data }) => {
      const optimalLen = data.length;
      const result = this.scoringSystem.calculateScore({
        gemsCollected: this.gemsCollected,
        goldenGemsCollected: this.goldenGemCollected ? 1 : 0,
        playerPathLen: this.stepsTaken,
        shortestPathLen: optimalLen,
        wallHits: this.wallHits,
        timeTaken: elapsed,
        totalGems: this.totalGems
      });

      result.breakdown = {};

      let mysteryBonus = 0;
      if (this.hasCompanion) {
        mysteryBonus = 50;
        result.score += mysteryBonus;
        result.breakdown.bonusFromCompanion = mysteryBonus;
        this.showFloatingText(this.player.x, this.player.y - 20, "+50 Bonus! 🧚‍♀️");
      }

      this.externalEvents?.emit("gameOver", {
        score: result.score,
        breakdown: result.breakdown,
        grade: result.grade,
        rank: result.rank,
        gemsCollected: this.gemsCollected,
        goldenGemCollected: this.goldenGemCollected,
        totalGems: this.totalGems,
        playerPathLen: this.stepsTaken,
        shortestPathLen: optimalLen,
        wallHits: this.wallHits,
        timeTaken: elapsed,
        difficulty: this.difficulty,
        mysteryBonus
      });
    };

    this.pathWorker.postMessage({
      maze: this.maze,
      start: this.startGrid,
      goal: this.goalGrid
    });
  }
}



  update() {
  if (!this.player) return;

  if (Phaser.Input.Keyboard.JustDown(this.cursors.left))  this.move(-1,  0);
  if (Phaser.Input.Keyboard.JustDown(this.cursors.right)) this.move( 1,  0);
  if (Phaser.Input.Keyboard.JustDown(this.cursors.up))    this.move( 0, -1);
  if (Phaser.Input.Keyboard.JustDown(this.cursors.down))  this.move( 0,  1);
}


  


  applyMysteryEffect(outcome) {
  if (!outcome || !outcome.action) {
    
    return;
  }

  // 🎯 Cleanup mystery box AFTER modal confirmation
  if (outcome.key) {
    this.mysteryBoxes?.delete(outcome.key);
    const boxEmoji = this.children.getByName(`box-${outcome.key}`);
    boxEmoji?.destroy();
  }

  // 🔥 Handle mystery effects
  this.showMysteryLabel(outcome.action);

  switch (outcome.action) {
    case "gemRain":
      this.spawnExtraGems(5);
      this.triggerGemShower();
      
      break;
    case "demonCurse":
      this.triggerDemonCurse();
      
      break;
    case "mysteryCompanion":
   // ✅ If already has companion, just reset the timer
  if (this.hasCompanion) {
    this.time.removeEvent(this.companionTimeout); // Cancel old timeout
    this.companionTimeout = this.time.delayedCall(20000, () => {
      this.hasCompanion = false;
      this.companionSprite?.destroy();
      this.companionSprite = null;
    });
    this.showMysteryLabel("mysteryCompanion");
    break;
  }
    this.hasCompanion = true;

  // 🧚 Create floating companion sprite
  this.companionSprite = this.add.text(
    this.player.x + 10,
    this.player.y - 10,
    "🧚",
    {
      fontSize: `${this.cellSize * 0.45}px`,
      fontStyle: "bold"
    }
  ).setOrigin(0.5).setDepth(2);

  // Companion follows player — optional: animate in `update()`

  // Set timer to remove after 20 seconds
  this.time.delayedCall(20000, () => {
    this.hasCompanion = false;
    this.companionSprite?.destroy();
    this.companionSprite = null;
  });

  this.showMysteryLabel("mysteryCompanion");
  break;

    case "controlsInverted":
      this.invertControls(15000);
      break;
    case "bonus":
      this.score += 30;
      this.maybeEmitScore();
      break;
    case "nothing":
    default:
      
  }
this.maybeEmitScore();
this.externalEvents?.emit("gemUpdate", this.gemsCollected, this.totalGems);

  // ✅ UNFREEZE the game
  this.scene.resume();               // <--- resume game
  this.mysteryBoxActive = false;    // <--- allow next box to trigger
  
}
playSoundSafe(key, options = {}) {
  try {
    const context = this.sound?.context;

    if (!context || context.state === "closed") {
      console.warn("🔇 AudioContext is closed. Cannot play sound:", key);
      return;
    }

    if (context.state === "suspended") {
      context.resume().then(() => this.sound.play(key, options));
    } else {
      this.sound.play(key, options);
    }
  } catch (e) {
    console.warn(`🎧 Sound '${key}' failed:`, e.message);
  }
}


showMysteryLabel(action) {
  const labelMap = {
    gemRain: "💎 Gem Shower!",
    demonCurse: "👹 Demon stole your gems!",
    mysteryCompanion: "🧚glowing fairy follows & draws gems to you!",
    controlsInverted: "🔁 Controls Reversed for 15s! 🌀 Mirror World! Left is right, up is down!",
    bonus: "🎉 Bonus +30 pts!",
    nothing: "🤷 Nothing happened!",
    controlsBack: "✅ Controls Normal!"
  };
  const msgText = labelMap[action] || "🌀 Unknown Effect!";
  const msg = this.add.text(
    this.player.x,
    this.player.y - this.cellSize,
    msgText,
    {
      fontSize: `${this.cellSize * 0.5}px`,
      color: "#000",
      fontStyle: "bold",
      backgroundColor: "#fff89a", // Soft yellow
      padding: { x: 10, y: 6 }
    }
  ).setOrigin(0.5).setDepth(10).setAlpha(0);

  this.tweens.add({
    targets: msg,
    alpha: 1,
    duration: 300,
    onComplete: () => {
      this.time.delayedCall(800, () => {
        this.tweens.add({
          targets: msg,
          y: msg.y - 20,
          alpha: 0,
          duration: 500,
          ease: "Cubic.easeIn",
          onComplete: () => msg.destroy()
        });
      });
    }
  });
}

spawnExtraGems(count = 5) {
  let placed = 0;
  let attempts = 0;

  const dropGem = (x, y) => {
    const gem = this.add.text(
      x * this.cellSize + this.cellSize / 2,
      y * this.cellSize + this.cellSize / 2,
      this.gemEmoji,
      { fontSize: `${this.cellSize * 0.5}px` }
    ).setOrigin(0.5);
    this.gemMap.set(`${x},${y}`, gem);
    
  };

  while (placed < count && attempts < 200) {
    const x = Phaser.Math.Between(1, this.cols - 2);
    const y = Phaser.Math.Between(1, this.rows - 2);
    const key = `${x},${y}`;
    if (this.maze[y][x] === 0 && !this.gemMap.has(key)) {
      dropGem(x, y);
      placed++;
    }
    attempts++;
  }

  this.totalGems += placed;
  this.externalEvents?.emit("gemUpdate", this.gemsCollected, this.totalGems);
}



invertControls(duration = 15000) {
  this.invertedControls = true;
  this.showMysteryLabel("controlsInverted");

  this.time.delayedCall(duration, () => {
    this.invertedControls = false;
    this.showMysteryLabel("controlsBack");
  });
}




modifySpeed(multiplier = 1.5, duration = 15000) {
  this.stepMultiplier = this.stepMultiplier / multiplier;
  this.time.delayedCall(duration, () => {
    this.stepMultiplier = this.stepMultiplier * multiplier;
    
  });
}


  // 🎯 NEW: Improved viewport management
  centerViewOnPlayer(immediate = false) {
    if (!this.player) return;
    
    // Get the game container's position relative to the page
    const gameCanvas = this.game.canvas;
    if (!gameCanvas) return;
    
    const canvasRect = gameCanvas.getBoundingClientRect();
    
    // Player's position in world coordinates
    const playerWorldX = this.player.x;
    const playerWorldY = this.player.y;
    
    // Convert to screen coordinates
    const playerScreenX = canvasRect.left + playerWorldX;
    const playerScreenY = canvasRect.top + playerWorldY + window.scrollY;
    
    // Account for UI elements
    const headerHeight = 120; // Header with score, gems, etc.
    const footerHeight = 80;  // Footer with instructions
    const sideBuffer = 50;    // Side margins
    
    // Calculate ideal scroll position to center player
    const idealScrollX = Math.max(0, playerScreenX - window.innerWidth / 2);
    const idealScrollY = Math.max(0, playerScreenY - headerHeight - (window.innerHeight - headerHeight - footerHeight) / 2);
    
    // Smooth scroll to center the player
    window.scrollTo({
      left: idealScrollX,
      top: idealScrollY,
      behavior: immediate ? 'auto' : 'smooth'
    });
  }

  updateViewport() {
    if (!this.player) return;

    // Get game canvas position
    const gameCanvas = this.game.canvas;
    if (!gameCanvas) return;
    
    const canvasRect = gameCanvas.getBoundingClientRect();
    
    // Player's screen position
    const playerScreenX = canvasRect.left + this.player.x;
    const playerScreenY = canvasRect.top + this.player.y + window.scrollY;
    
    // Define viewport boundaries
    const headerHeight = 120;
    const footerHeight = 80;
    const buffer = 150; // Distance from edge before scrolling
    
    const viewportTop = window.scrollY + headerHeight + buffer;
    const viewportBottom = window.scrollY + window.innerHeight - footerHeight - buffer;
    const viewportLeft = buffer;
    const viewportRight = window.innerWidth - buffer;
    
    // Check if player is approaching viewport edges
    const needsScroll = 
      playerScreenY < viewportTop ||
      playerScreenY > viewportBottom ||
      playerScreenX < viewportLeft ||
      playerScreenX > viewportRight;
    
    if (needsScroll) {
      this.centerViewOnPlayer(false);
    }
  }
showFloatingText(x, y, text) {
  const floating = this.add.text(x, y, text, {
    font: "16px Arial",
    fill: "#FFD700",
    stroke: "#000",
    strokeThickness: 2,
  }).setDepth(1000);

  this.tweens.add({
    targets: floating,
    y: y - 30,
    alpha: 0,
    duration: 1200,
    ease: "easeOut",
    onComplete: () => floating.destroy(),
  });
}
  // 🔧 FIXED: Only emit score number, not object
  maybeEmitScore() {
    if (this.time.now - this.lastScoreEmit > 100) {
      this.externalEvents?.emit("scoreChanged", this.score);
      this.lastScoreEmit = this.time.now;
    }
  }

  isValidMove(x, y) {
    return this.maze?.[y]?.[x] === 0;
  }

  calculateScore(optLen, time) {
    const stats = {
      gemsCollected: this.gemsCollected,
      goldenGemsCollected: this.goldenGemCollected ? 1 : 0,
      playerPathLen: this.stepsTaken,
      shortestPathLen: optLen,
      wallHits: this.wallHits,
      timeTaken: time,
      totalGems: this.totalGems
    };
    
    const result = this.scoringSystem.calculateScore(stats);
    return result.score; // Return just the score for compatibility
  }

  createMaze() {
    const customSettings = JSON.parse(localStorage.getItem("maze-settings") || "{}");
    
    // 🎯 VIEWPORT CONSTRAINTS: Calculate max maze size that fits on screen
    const headerHeight = 120;
    const footerHeight = 100;
    const sideMargin = 50;
    
    const availableWidth = window.innerWidth - (sideMargin * 2);
    const availableHeight = window.innerHeight - headerHeight - footerHeight;
    
    const maxCols = Math.floor(availableWidth / this.cellSize) - 2; // Reserve space for borders
    const maxRows = Math.floor(availableHeight / this.cellSize) - 2; // Reserve space for borders
    
    
    
    // Difficulty defaults
    const difficultyDefaults = {
      easy: { rows: 15, cols: 15 },
      medium: { rows: 21, cols: 21 },
      hard: { rows: 31, cols: 31 }
    };
    
    const defaults = difficultyDefaults[this.difficulty] || difficultyDefaults.medium;
    
    // 🔧 ALWAYS set this.rows and this.cols
    if (customSettings.rows && customSettings.columns && 
        (customSettings.rows !== defaults.rows || customSettings.columns !== defaults.cols)) {
      this.rows = customSettings.rows;
      this.cols = customSettings.columns;
     
    } else {
      this.rows = defaults.rows;
      this.cols = defaults.cols;
      
    }

    // 🚨 IMPORTANT: Make sure this.rows and this.cols are numbers
    this.rows = Number(this.rows);
    this.cols = Number(this.cols);
    
    // 🎯 CONSTRAIN MAZE SIZE: Ensure maze fits within viewport
    const originalRows = this.rows;
    const originalCols = this.cols;
    
    this.rows = Math.min(this.rows, maxRows);
    this.cols = Math.min(this.cols, maxCols);
    
    // Ensure odd numbers for proper maze generation
    if (this.rows % 2 === 0) this.rows--;
    if (this.cols % 2 === 0) this.cols--;
    
    // Minimum size constraints
    this.rows = Math.max(15, this.rows);
    this.cols = Math.max(15, this.cols);
    
    if (originalRows !== this.rows || originalCols !== this.cols) {
      
    }
    
    // 🔧 Update the game canvas size to match the actual maze
    this.actualMazeWidth = this.cols * this.cellSize;
    this.actualMazeHeight = this.rows * this.cellSize;
    
    // Resize the game canvas to exactly fit the maze
    if (this.game && this.game.canvas) {
      this.game.scale.resize(this.actualMazeWidth, this.actualMazeHeight);
    }
    
    // Now continue with maze creation...
    this.maze = Array.from({ length: this.rows }, () => Array(this.cols).fill(1));

    // 🌀 PATH COMPLEXITY based on difficulty
    const carve = (x, y) => {
      let dirs = [ {dx:0,dy:-2}, {dx:2,dy:0}, {dx:0,dy:2}, {dx:-2,dy:0} ];
      
      // 🎮 DIFFICULTY-BASED PATH GENERATION
      if (this.difficulty === 'easy') {
        // Easy: More predictable, straighter paths
        dirs = dirs.sort(() => Math.random() - 0.2); // Less randomness
        
        // 25% chance to create wider paths (remove extra walls)
        if (Math.random() < 0.25) {
          dirs = dirs.slice(0, 2); // Use fewer directions = straighter paths
        }
        
      } else if (this.difficulty === 'hard') {
        // Hard: More chaotic, winding paths
        dirs = dirs.sort(() => Math.random() - 0.8); // More randomness
        
        // Add diagonal bias for more complex patterns
        if (Math.random() < 0.3) {
          dirs = [...dirs, ...dirs]; // Double up directions = more turns
        }
        
      } else {
        // Medium: Balanced randomness
        dirs = dirs.sort(() => Math.random() - 0.5);
      }
      
      for (const {dx, dy} of dirs) {
        const nx = x + dx, ny = y + dy;
        if (ny > 0 && ny < this.rows - 1 && nx > 0 && nx < this.cols - 1 && this.maze[ny][nx] === 1) {
          this.maze[ny][nx] = 0;
          this.maze[y + dy / 2][x + dx / 2] = 0;
          carve(nx, ny);
        }
      }
    };

    this.startGrid = { x: 1, y: 1 };
    this.goalGrid  = { x: this.cols - 2, y: this.rows - 2 };
    this.maze[1][1] = 0;
    carve(1, 1);

    // 🔄 LOOPS/SHORTCUTS based on difficulty AND preset
    let loopsToRemove;
    
    if (this.difficulty === 'easy') {
      // Easy: More shortcuts (easier navigation)
      loopsToRemove = Math.max(this.loopsToRemove, 12);
    } else if (this.difficulty === 'hard') {
      // Hard: Fewer shortcuts (more dead ends)
      loopsToRemove = Math.min(this.loopsToRemove, 2);
    } else {
      // Medium: Use preset value
      loopsToRemove = this.loopsToRemove;
    }

    let removed = 0;
    while (removed < loopsToRemove) {
      const x = Phaser.Math.Between(1, this.cols - 2);
      const y = Phaser.Math.Between(1, this.rows - 2);
      if (this.maze[y][x] === 1) { 
        this.maze[y][x] = 0; 
        removed++; 
      }
    }
    
    
  }

  drawMaze() {
    

    const s = this.cellSize;
    for (let y = 0; y < this.rows; y++) {
      for (let x = 0; x < this.cols; x++) {
        if (this.maze[y][x]) {
          this.add.rectangle(
            Math.round(x * s),
            Math.round(y * s),
            Math.ceil(s),
            Math.ceil(s),
            0x000000
          ).setOrigin(0);
        }
      }
    }
    this.mysteryBoxes = new Set();

while (this.mysteryBoxes.size < 3) {
  const x = Phaser.Math.Between(1, this.cols - 2);
  const y = Phaser.Math.Between(1, this.rows - 2);
  const key = `${x},${y}`;

  if (this.maze[y][x] === 0 && key !== `${this.startGrid.x},${this.startGrid.y}`) {
    this.mysteryBoxes.add(key);

    const box = this.add.text(
      x * this.cellSize + this.cellSize / 2,
      y * this.cellSize + this.cellSize / 2,
      "🎁",
      { fontSize: `${this.cellSize * 0.5}px` }
    ).setOrigin(0.5);

    box.setName(`box-${key}`);
  }
}




    const W = this.cols * this.cellSize;
    const H = this.rows * this.cellSize;
    const EDGE = 1;
    this.add.rectangle(0, 0, EDGE, H, 0x000000).setOrigin(0);              // left
    this.add.rectangle(0, 0, W, EDGE, 0x000000).setOrigin(0);              // top
    this.add.rectangle(W - EDGE, 0, EDGE, H - EDGE, 0x000000).setOrigin(0); // right
    this.add.rectangle(0, H - EDGE, W - EDGE, EDGE, 0x000000).setOrigin(0); // bottom
  }

  setGoal() {
  const queue = [{ x: this.startGrid.x, y: this.startGrid.y, dist: 0 }];
  const visited = new Set([`${this.startGrid.x},${this.startGrid.y}`]);
  let farthest = { ...this.startGrid, dist: 0 };

  const directions = [
    { dx: 1, dy: 0 },
    { dx: -1, dy: 0 },
    { dx: 0, dy: 1 },
    { dx: 0, dy: -1 }
  ];

  while (queue.length > 0) {
    const current = queue.shift();
    for (const { dx, dy } of directions) {
      const nx = current.x + dx;
      const ny = current.y + dy;
      const key = `${nx},${ny}`;

      if (
        nx > 0 && ny > 0 &&
        nx < this.cols - 1 && ny < this.rows - 1 &&
        this.maze[ny][nx] === 0 &&
        !visited.has(key)
      ) {
        visited.add(key);
        const next = { x: nx, y: ny, dist: current.dist + 1 };
        queue.push(next);

        if (next.dist > farthest.dist) {
          farthest = next;
        }
      }
    }
  }

  this.goalGrid = { x: farthest.x, y: farthest.y };
}

getGoalSymbol() {
  const mapping = {
    "🦄": "✨",
    "⛵": "🏝️",
    "🐬": "🔶",
    "🧑‍🚀": "🚀",
    "🧜": "🔮",
    "🐘": "🌳"
  };
  return mapping[this.playerEmoji] || "🎯"; // fallback
}

  
drawGoal() {
  const symbol = this.getGoalSymbol();
  this.goalEmoji = this.add.text(
    this.goalGrid.x * this.cellSize + this.cellSize / 2,
    this.goalGrid.y * this.cellSize + this.cellSize / 2,
    symbol,
    { fontSize: `${this.cellSize * 0.6}px` }
  ).setOrigin(0.5).setDepth(1);
  
}

movePlayer(dir) {
  const dx = dir === "left" ? -1 : dir === "right" ? 1 : 0;
  const dy = dir === "up" ? -1 : dir === "down" ? 1 : 0;
  this.move(dx, dy);
}



  createPlayer() {
    this.playerGrid = { ...this.startGrid };
    this.player = this.add.text(
      this.playerGrid.x * this.cellSize + this.cellSize / 2,
      this.playerGrid.y * this.cellSize + this.cellSize / 2,
      this.playerEmoji,
      { fontSize: `${this.cellSize * 0.6}px` }
    ).setOrigin(0.5).setDepth(2);
  }

  createGems() {
    this.gemMap = new Map();

    const totalCells = this.rows * this.cols;
    
    // Calculate gems based on difficulty and maze size
    let gemCount;
    if (this.difficulty === "easy") {
      gemCount = Math.round(totalCells / 25);  // More gems for easy
    } else if (this.difficulty === "medium") {
      gemCount = Math.round(totalCells / 35);  // Moderate gems
    } else { // hard
      gemCount = Math.round(totalCells / 45);  // Fewer gems for hard
    }
    
    // 🔧 ENSURE PROPER GEM COUNT: Minimum 5 gems, maximum 1 gem per 12 cells
    gemCount = Math.max(5, Math.min(gemCount, Math.round(totalCells / 12)));
    
    
    
    
    let placed = 0;

    const canPlace = (x, y) => {
      const key = `${x},${y}`;
      return (
        this.maze[y][x] === 0 &&
        !this.gemMap.has(key) &&
        !(x === this.startGrid.x && y === this.startGrid.y) &&
        !(x === this.goalGrid.x && y === this.goalGrid.y)
      );
    };

    const dropGem = (x, y, symbol = this.gemEmoji) => {
      const gem = this.add.text(
        x * this.cellSize + this.cellSize / 2,
        y * this.cellSize + this.cellSize / 2,
        symbol,
        { fontSize: `${this.cellSize * 0.5}px` }
      ).setOrigin(0.5);
      this.gemMap.set(`${x},${y}`, gem);
      
    };

    // Place regular gems
    let attempts = 0;
    while (placed < gemCount && attempts < 1000) {
      const x = Phaser.Math.Between(1, this.cols - 2);
      const y = Phaser.Math.Between(1, this.rows - 2);
      if (canPlace(x, y)) {
        dropGem(x, y);
        placed++;
      }
      attempts++;
    }

    // 🔧 FIXED: Replace one random gem with golden gem if we have gems
    if (this.gemMap.size > 0) {
      const keys = Array.from(this.gemMap.keys());
      const luckyIndex = Math.floor(Math.random() * keys.length);
      const lucky = keys[luckyIndex];
      const [gx, gy] = lucky.split(",").map(Number);
      
      // Remove the regular gem and place golden gem
      this.gemMap.get(lucky).destroy();
      dropGem(gx, gy, "🌟");
      this.goldenGemKey = lucky;
      
      
    }
    
    // 🔧 IMPORTANT: Track total gems for scoring and display
    this.totalGems = this.gemMap.size;
    this.externalEvents?.emit("gemUpdate", this.gemsCollected, this.totalGems);
    
  }
}